import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Collapse,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useFormik } from "formik";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  generatePayrollRun,
  getEligibilityCheck,
} from "../services/PayrollService";
import generatePayrollRunSchema from "./generatePayrollRun.schema";
import type {
  EligibilityCheckResult,
  SkippedEmployee,
} from "../types/payroll.types";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const REASON_LABELS: Record<string, string> = {
  missing_join_date: "No join date",
  missing_employment_category: "No employment category",
  no_active_salary: "No active salary structure",
};

function reasonLabel(r: string): string {
  return REASON_LABELS[r] ?? r;
}

function periodLabel(month: number, year: number): string {
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return `${fmt(start)} – ${fmt(end)}`;
}

function exportEligibilityCsv(
  eligibility: EligibilityCheckResult,
  month: number,
  year: number,
): void {
  const UTF8_BOM = "﻿";
  const escape = (v: string) =>
    /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;

  const header = ["Code", "Name", "Status", "Reasons"];
  const lines = [header.join(",")];

  for (const emp of eligibility.eligible) {
    lines.push(
      [
        escape(emp.employeeCode ?? ""),
        escape(emp.name),
        "Eligible",
        "",
      ].join(","),
    );
  }
  for (const emp of eligibility.ineligible) {
    lines.push(
      [
        escape(emp.employeeCode ?? ""),
        escape(emp.name),
        "Ineligible",
        escape(emp.reasons.map(reasonLabel).join("; ")),
      ].join(","),
    );
  }

  const csv = UTF8_BOM + lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `eligibility_${year}_${String(month).padStart(2, "0")}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const now = new Date();
const YEAR_OPTIONS: number[] = Array.from(
  { length: 8 },
  (_, i) => now.getFullYear() - 2 + i,
);

type Step = "dates" | "eligibility" | "done";

const STEPS: { key: Step; label: string }[] = [
  { key: "dates", label: "Select period" },
  { key: "eligibility", label: "Review" },
  { key: "done", label: "Done" },
];

const StepIndicator = ({ current }: { current: Step }) => {
  const idx = STEPS.findIndex((s) => s.key === current);
  return (
    <div className="d-flex align-items-center justify-content-center gap-2 mb-4">
      {STEPS.map((s, i) => {
        const active = i === idx;
        const done = i < idx;
        return (
          <div key={s.key} className="d-flex align-items-center gap-2">
            <div className="d-flex flex-column align-items-center">
              <div
                className={`rounded-circle d-flex align-items-center justify-content-center fw-bold`}
                style={{
                  width: 28,
                  height: 28,
                  fontSize: 12,
                  background: done ? "var(--vz-success)" : active ? "var(--vz-primary)" : "var(--vz-light)",
                  color: done || active ? "#fff" : "var(--vz-secondary-color)",
                  border: active ? "2px solid var(--vz-primary)" : "2px solid transparent",
                }}
              >
                {done ? <i className="ri-check-line" style={{ fontSize: 13 }} /> : i + 1}
              </div>
              <span
                className="mt-1"
                style={{
                  fontSize: 11,
                  fontWeight: active ? 600 : 400,
                  color: active ? "var(--vz-primary)" : done ? "var(--vz-success)" : "var(--vz-secondary-color)",
                }}
              >
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  width: 40,
                  height: 2,
                  marginBottom: 18,
                  background: i < idx ? "var(--vz-success)" : "var(--vz-border-color)",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const GeneratePayrollRunModal = ({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (runId: number) => void;
}) => {
  const [step, setStep] = useState<"dates" | "eligibility">("dates");
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibility, setEligibility] = useState<EligibilityCheckResult | null>(null);
  const [skipped, setSkipped] = useState<SkippedEmployee[]>([]);
  const [generatedRunId, setGeneratedRunId] = useState<number | null>(null);
  const [ineligibleOpen, setIneligibleOpen] = useState(false);

  const handleClose = (completedRunId?: number) => {
    const runId = completedRunId ?? generatedRunId;
    setStep("dates");
    setEligibility(null);
    setSkipped([]);
    setGeneratedRunId(null);
    formik.resetForm();
    onClose();
    if (runId != null) {
      onCreated(runId);
    }
  };

  const formik = useFormik({
    initialValues: {
      month: now.getMonth() + 1,
      year: now.getFullYear(),
    },
    validationSchema: generatePayrollRunSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const res = await generatePayrollRun({
          month: values.month,
          year: values.year,
        });
        if (res.success) {
          const skippedList = res.data?.skipped ?? [];
          const id = res.data?.payrollRun?.id;
          if (skippedList.length > 0) {
            setSkipped(skippedList);
            if (id != null) setGeneratedRunId(id);
          } else {
            toast.success(res.message || "Payroll run generated");
            handleClose(id ?? undefined);
          }
        } else {
          toast.error(res.message || "Could not generate payroll run");
        }
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        toast.error(msg || "Could not generate payroll run");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleNext = async () => {
    await formik.validateForm();
    formik.setTouched({ month: true, year: true });
    if (formik.errors.month || formik.errors.year) return;

    setEligibilityLoading(true);
    try {
      const res = await getEligibilityCheck(formik.values.month, formik.values.year);
      if (res.success && res.data) {
        setEligibility(res.data);
        setStep("eligibility");
      } else {
        toast.error(res.message || "Could not check eligibility");
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || "Could not check eligibility");
    } finally {
      setEligibilityLoading(false);
    }
  };

  const eligibleCount = eligibility?.eligible.length ?? 0;
  const ineligibleCount = eligibility?.ineligible.length ?? 0;
  const isSkippedView = skipped.length > 0;

  const currentStep: Step = isSkippedView ? "done" : step;

  return (
    <Modal isOpen={isOpen} toggle={() => handleClose()} centered size="lg">
      <ModalHeader toggle={() => handleClose()}>Generate payroll run</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <StepIndicator current={currentStep} />

          {isSkippedView ? (
            <>
              <div className="text-center mb-4">
                <div className="avatar-md mx-auto mb-3">
                  <span className="avatar-title rounded-circle bg-success-subtle text-success">
                    <i className="ri-check-double-line fs-20" />
                  </span>
                </div>
                <h5 className="mb-1">Run created</h5>
                <p className="text-muted mb-0">
                  {MONTH_NAMES[formik.values.month - 1]} {formik.values.year} payroll run was generated successfully.
                </p>
                {generatedRunId != null && (
                  <Link
                    to={`/finance/payroll/${generatedRunId}`}
                    className="btn btn-sm btn-soft-primary mt-2"
                    onClick={() => handleClose()}
                  >
                    <i className="ri-eye-line align-bottom me-1" />
                    View run
                  </Link>
                )}
              </div>
              {ineligibleCount > 0 && (
                <div className="border rounded mt-3">
                  <button
                    type="button"
                    className="btn btn-link w-100 text-start d-flex align-items-center justify-content-between px-3 py-2 text-decoration-none"
                    onClick={() => setIneligibleOpen((o) => !o)}
                  >
                    <span className="text-muted fs-12 text-uppercase fw-semibold">
                      Ineligible employees
                      <Badge color="warning" pill className="ms-2">
                        {ineligibleCount}
                      </Badge>
                    </span>
                    <i
                      className={`ri-arrow-${ineligibleOpen ? "up" : "down"}-s-line text-muted`}
                    />
                  </button>
                  <Collapse isOpen={ineligibleOpen}>
                    <Table size="sm" bordered className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Employee</th>
                          <th>Reasons</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibility!.ineligible.map((emp, i) => (
                          <tr key={emp.employeeId ?? i}>
                            <td>
                              {emp.name}
                              {emp.employeeCode && (
                                <span className="text-muted ms-1 small">
                                  ({emp.employeeCode})
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {emp.reasons.map((r, j) => (
                                  <Badge key={j} color="warning" pill>
                                    {reasonLabel(r)}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Collapse>
                </div>
              )}
            </>
          ) : step === "dates" ? (
            <>
              <Row className="g-3">
                <Col md={6}>
                  <Label className="form-label">Month</Label>
                  <Input
                    type="select"
                    name="month"
                    value={formik.values.month}
                    onChange={(e) =>
                      formik.setFieldValue("month", Number(e.target.value))
                    }
                    onBlur={formik.handleBlur}
                    invalid={!!(formik.touched.month && formik.errors.month)}
                  >
                    {MONTH_NAMES.map((name, i) => (
                      <option key={i + 1} value={i + 1}>
                        {name}
                      </option>
                    ))}
                  </Input>
                  {formik.touched.month && formik.errors.month && (
                    <FormFeedback>{formik.errors.month}</FormFeedback>
                  )}
                </Col>
                <Col md={6}>
                  <Label className="form-label">Year</Label>
                  <Input
                    type="select"
                    name="year"
                    value={formik.values.year}
                    onChange={(e) =>
                      formik.setFieldValue("year", Number(e.target.value))
                    }
                    onBlur={formik.handleBlur}
                    invalid={!!(formik.touched.year && formik.errors.year)}
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </Input>
                  {formik.touched.year && formik.errors.year && (
                    <FormFeedback>{formik.errors.year}</FormFeedback>
                  )}
                </Col>
              </Row>
              <div className="mt-3 p-3 bg-light rounded d-flex align-items-center gap-2">
                <i className="ri-calendar-line text-muted fs-16" />
                <div>
                  <span className="text-muted small">Payroll period: </span>
                  <span className="fw-medium small">
                    {periodLabel(formik.values.month, formik.values.year)}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="d-flex align-items-center gap-2 mb-4 p-3 bg-light rounded">
                <i className="ri-calendar-check-line text-primary fs-16" />
                <div>
                  <span className="text-muted small">Payroll period: </span>
                  <span className="fw-medium small">
                    {periodLabel(formik.values.month, formik.values.year)}
                  </span>
                </div>
              </div>

              <Row className="g-3 mb-4">
                <Col xs={6}>
                  <div className="p-3 rounded border text-center">
                    <div className="fs-2 fw-bold text-success">{eligibleCount}</div>
                    <div className="text-muted small mt-1">
                      <i className="ri-user-follow-line me-1" />
                      Eligible
                    </div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="p-3 rounded border text-center">
                    <div className="fs-2 fw-bold text-warning">{ineligibleCount}</div>
                    <div className="text-muted small mt-1">
                      <i className="ri-user-forbid-line me-1" />
                      Ineligible
                    </div>
                  </div>
                </Col>
              </Row>

              {eligibleCount === 0 ? (
                <Alert color="danger" className="mb-3">
                  <i className="ri-close-circle-line me-2" />
                  No eligible employees found for this period. Resolve the issues below before generating.
                </Alert>
              ) : ineligibleCount === 0 ? (
                <Alert color="success" className="mb-3">
                  <i className="ri-checkbox-circle-line me-2" />
                  All <strong>{eligibleCount}</strong> employees are eligible. Ready to generate.
                </Alert>
              ) : (
                <Alert color="info" className="mb-3">
                  <i className="ri-information-line me-2" />
                  <strong>{eligibleCount}</strong> employees will be included. The <strong>{ineligibleCount}</strong> ineligible employee{ineligibleCount !== 1 ? "s" : ""} below will be skipped.
                </Alert>
              )}

              <Alert color="info" className="mb-3 border-0" fade={false}>
                <div className="d-flex gap-2">
                  <i className="ri-information-line fs-16 flex-shrink-0 mt-1" />
                  <div>
                    <strong>Leave & attendance deductions are not automatic.</strong>{" "}
                    Review and adjust each employee's figures manually before finalizing the run.
                  </div>
                </div>
              </Alert>

              {ineligibleCount > 0 && (
                <div className="border rounded">
                  <button
                    type="button"
                    className="btn btn-link w-100 text-start d-flex align-items-center justify-content-between px-3 py-2 text-decoration-none"
                    onClick={() => setIneligibleOpen((o) => !o)}
                  >
                    <span className="text-muted fs-12 text-uppercase fw-semibold">
                      Ineligible employees
                      <Badge color="warning" pill className="ms-2">
                        {ineligibleCount}
                      </Badge>
                    </span>
                    <i
                      className={`ri-arrow-${ineligibleOpen ? "up" : "down"}-s-line text-muted`}
                    />
                  </button>
                  <Collapse isOpen={ineligibleOpen}>
                    <Table size="sm" bordered className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Employee</th>
                          <th>Reasons</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibility!.ineligible.map((emp, i) => (
                          <tr key={emp.employeeId ?? i}>
                            <td>
                              {emp.name}
                              {emp.employeeCode && (
                                <span className="text-muted ms-1 small">
                                  ({emp.employeeCode})
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {emp.reasons.map((r, j) => (
                                  <Badge key={j} color="warning" pill>
                                    {reasonLabel(r)}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Collapse>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {isSkippedView ? (
            <Button
              type="button"
              color="primary"
              onClick={() => handleClose()}
            >
              Done
            </Button>
          ) : step === "dates" ? (
            <>
              <Button type="button" color="light" onClick={() => handleClose()}>
                Cancel
              </Button>
              <Button
                type="button"
                color="primary"
                disabled={eligibilityLoading}
                onClick={handleNext}
              >
                {eligibilityLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Checking eligibility…
                  </>
                ) : (
                  <>
                    Check eligibility
                    <i className="ri-arrow-right-line align-bottom ms-1" />
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" color="light" onClick={() => setStep("dates")}>
                <i className="ri-arrow-left-line align-bottom me-1" />
                Back
              </Button>
              <Button
                type="button"
                color="light"
                className="ms-auto"
                onClick={() =>
                  exportEligibilityCsv(eligibility!, formik.values.month, formik.values.year)
                }
              >
                <i className="ri-download-2-line align-bottom me-1" />
                Export CSV
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={formik.isSubmitting || eligibleCount === 0}
              >
                {formik.isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Generating…
                  </>
                ) : (
                  <>
                    <i className="ri-play-line align-bottom me-1" />
                    Generate run
                  </>
                )}
              </Button>
            </>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default GeneratePayrollRunModal;